/**
 * Record-access URL-param **reader** (DSCO-5543 — Node Based Access Control).
 *
 * The query key is **never** a module constant: it is the active dimension's
 * `name` read verbatim off `/me.accessControl.dimensions[0]` (HLD §B), so a
 * team-based deployment uses `?team=` and a node-based one `?node=`. No
 * pluralisation is applied — the historical `?teams=` key does not survive
 * Phase 1 (bookmark redirects are a separate follow-up).
 *
 * This helper does not read `/me` itself. The caller owns that read (normally
 * `useCurrentUser().activeDimension`) and passes `dimensionName` down, which
 * keeps it pure and trivially testable per dimension.
 *
 * **Writing** the param is no longer this module's job: the matching
 * `appendAccessDimensionTo*` helpers were deleted in DP-3188 (AC8) in favour of
 * `useDashboardNavigate` / `<DashboardLink>`, which preserve the whole query —
 * dimension param included — for every in-app navigation, rather than
 * re-appending one special-cased key per call site.
 */

const HASH_SEPARATOR = '#';
const QUERY_SEPARATOR = '?';

/**
 * Guard on the serialized param value — ids are opaque but always drawn from
 * this alphabet, so anything else is treated as a tampered URL and dropped.
 */
const ACCESS_DIMENSION_VALUE_PATTERN = /^[A-Za-z0-9_,.-]+$/;

/**
 * Accept either an already-parsed bag (`useSearchParams()`) or a raw
 * URL / query string, so callers outside the router (bookmark handling,
 * link rewriting) do not have to parse first.
 */
function toSearchParams(url: URLSearchParams | string): URLSearchParams {
  if (typeof url !== 'string') {
    return url;
  }
  // Hash first, then query — not the other way round. A `?` inside a fragment
  // is part of the fragment, not a query delimiter, so locating `?` before
  // stripping the hash made `/list#tab?team=evil` parse the fragment's contents
  // as real params. A fragment is opaque to this reader by definition.
  const hashStart = url.indexOf(HASH_SEPARATOR);
  const withoutHash = hashStart === -1 ? url : url.slice(0, hashStart);
  const queryStart = withoutHash.indexOf(QUERY_SEPARATOR);
  // No `?` at all means the caller handed over a bare query string
  // (`team=a,b`) rather than a URL — a documented input for this helper.
  const query =
    queryStart === -1 ? withoutHash : withoutHash.slice(queryStart + 1);
  return new URLSearchParams(query);
}

/**
 * Read the active dimension's selection off a URL.
 *
 * @returns the raw comma-joined value, or `null` when the param is absent or
 *   fails validation. An absent param means "all selected" by convention.
 */
export function extractAccessDimensionParam({
  url,
  dimensionName,
}: {
  url: URLSearchParams | string;
  dimensionName: string;
}): string | null {
  const raw = toSearchParams(url).get(dimensionName);
  return raw && ACCESS_DIMENSION_VALUE_PATTERN.test(raw) ? raw : null;
}
